/**
 * Small helpers for loading media through plain DOM elements, shared by
 * playback, the media pickers and migration. No store access.
 */
import { addCacheBuster } from '@/helpers/runtimeHelper';

/**
 * Load an image and hand back the element, so the caller can read its real
 * size. Rejects if it will not load.
 *
 * Pass `crossOrigin: true` when the image will be drawn onto a canvas.
 * Without it, the canvas becomes unreadable and export cannot copy pixels
 * off it.
 */
export function preloadImage(
  url: string,
  options?: { crossOrigin?: boolean }
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (options?.crossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () =>
      reject(new Error(`Failed to load image: ${url}`))
    );
    img.src = url;
  });
}

/**
 * A time far past the end of any file. Seeking here makes a source that
 * reports no length work one out: the browser clamps to the real end and
 * announces the duration it found.
 */
const FORCE_DURATION_SEEK_S = 1e101;

/** How long to wait for that, so an unseekable file cannot stall an upload. */
const DURATION_RESOLVE_TIMEOUT_MS = 3000;

/**
 * A video's real size and length, read from its metadata alone.
 * Rejects when the metadata will not load.
 *
 * Getting the length right matters: it is saved onto the layer as its
 * duration and trim end, and caps that clip's playback and its row on the
 * ruler until something corrects the record.
 *
 * Some files answer the first metadata read with no usable length and revise
 * it moments later. Rather than saving that, this coaxes a real answer out of
 * the browser by seeking past the end and waiting for it to report back.
 *
 * If nothing arrives in time it gives back a length of 0, which leaves the
 * trim end unset and lets playback fall back to whatever the live element
 * reports. A missing length recovers on its own; a wrong one is what leaves a
 * clip stuck playing half of itself.
 */
export function preloadVideo(
  url: string
): Promise<{ width: number; height: number; duration: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = addCacheBuster(url);
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';

    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('error', onError);
      video.src = '';
    };

    const finish = (duration: number) => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration
      });
      cleanup();
    };

    const hasRealDuration = () =>
      Number.isFinite(video.duration) && video.duration > 0;

    const onLoadedMetadata = () => {
      if (hasRealDuration()) {
        finish(video.duration);
        return;
      }
      // No usable length yet. Nudge the browser into working one out, and
      // let the duration-changed handler below pick up the answer.
      timer = setTimeout(() => finish(0), DURATION_RESOLVE_TIMEOUT_MS);
      try {
        video.currentTime = FORCE_DURATION_SEEK_S;
      } catch {
        // The file cannot be seeked at all; the timeout above is the answer.
      }
    };

    const onDurationChange = () => {
      if (hasRealDuration()) finish(video.duration);
    };

    const onError = () => {
      reject(new Error('Failed to load video metadata'));
      cleanup();
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('error', onError);
  });
}

/**
 * Whether an audio element needs replacing: there is no element yet, or the
 * one there is points at a different file than the layer does.
 *
 * The comparison is made on full absolute URLs, because an element always
 * reports its address that way even when it was given a relative one.
 */
export function hasAudioSourceChanged(
  audioEl: HTMLAudioElement | undefined,
  url: string | undefined
): boolean {
  if (!audioEl) return true;
  if (!url) return true;
  try {
    const absoluteLayerUrl = new URL(url, window.location.origin).href;
    return audioEl.src !== absoluteLayerUrl;
  } catch {
    return audioEl.src !== url;
  }
}
